// chat/actions.ts — the chat control EXECUTOR (cloned + adapted from the recipe_box
// scaffold). The client router (chat/brain.ts) maps ONE user message → ONE ChatAction;
// this file EXECUTES that action against THIS app's on-device store (src/store.ts).
// Every call is LOCAL SQLite over Tauri IPC — no network, no billing. (Running a mock /
// getting feedback stays on the main screen where the full spoken flow lives.)
//
// The action set is DERIVED FROM THIS STORE: it exposes listSessions / deleteSession /
// bandTrend, so the chat can search sessions · show progress · delete-with-confirm.
//
// HITL discipline (mirrors Expense): the destructive action (delete_session) is NEVER
// executed from a chat turn on its own — the Chat UI holds it and requires an explicit
// Confirm tap. This executor just performs the delete once the UI hands it back.

import { store, relativeDays, bandRange, EXAM_LABELS, MODE_LABELS, type Session, type Exam } from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "ielts-speaking-coach";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave, getQuestions, BrainError } from "../brain";
import { addInstruction } from "./brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

// A compact session row rendered inline in a chat bubble (filter results).
export interface FilterRow {
  id: string;
  exam: string;
  mode: string;
  topic: string;
  answered_count: number;
  turn_count: number;
  band: string;
  created_at: string;
}

// A purely LOCAL filter over the user's own practice history (no server route).
export interface SessionFilter {
  /** Free-text contains over topic. */
  text?: string;
  exam?: string;
  mode?: string;
  /** Only sessions with unanswered turns. */
  inProgress?: boolean;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "filter"; filter: SessionFilter; label?: string }
  | { kind: "progress" }
  | { kind: "delete_session"; id: string; label?: string } // UI confirms before this reaches executeAction
  // Retitle a session. Not destructive — nothing the customer recorded is touched, only the label
  // they find it by, so it does NOT go through the confirm gate.
  | { kind: "update_session"; id: string; topic: string; label?: string }
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  // G16 — the app's /questions endpoint was UI-only: the chat could not produce a single
  // practice question. Asking an examiner for a question is the most obvious thing a student
  // types, and it answered "practice starts from the mode cards".
  | { kind: "practice_questions"; exam: Exam; part: "p1" | "p2" | "p3" | "interview"; topic?: string }
  | { kind: "remember"; instruction: string }
  // ── the LOCAL lesson library (agent layer, 2026-08-12) ──────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string } // UI confirms before executeAction
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };

export interface ActionResult {
  ok: boolean;
  message: string;   // shown back in the chat
  changed?: boolean; // the history changed → the main list should refresh
  rows?: FilterRow[]; // filter matches to render inline
}

const norm = (s: string) => s.trim().toLowerCase();

/** Apply a SessionFilter locally — every condition ANDs; empty filter matches all. */
export function applySessionFilter(sessions: Session[], f: SessionFilter): Session[] {
  return sessions.filter((s) => {
    if (f.exam && s.exam !== f.exam) return false;
    if (f.mode && s.mode !== f.mode) return false;
    if (f.inProgress && s.answered_count >= s.turn_count) return false;
    if (f.text && !norm(s.topic).includes(norm(f.text))) return false;
    return true;
  });
}

const toRow = (s: Session): FilterRow => ({
  id: s.id,
  exam: s.exam,
  mode: s.mode,
  topic: s.topic,
  answered_count: s.answered_count,
  turn_count: s.turn_count,
  band: bandRange(s.band_low, s.band_high),
  created_at: s.created_at,
});

export async function executeAction(a: ChatAction, ctx?: { transcript?: string }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "filter": {
        const all = await store.listSessions({ limit: 50000 });
        const matches = applySessionFilter(all, a.filter);
        const forLabel = a.label ? ` for "${a.label}"` : "";
        return {
          ok: true,
          message: `${matches.length} session${matches.length !== 1 ? "s" : ""}${forLabel}` + (matches.length > 50 ? " (showing first 50)" : ""),
          rows: matches.slice(0, 50).map(toRow),
        };
      }

      case "progress": {
        // Purely local aggregation of the user's OWN history — no billed call.
        const sessions = await store.listSessions({ limit: 50000 });
        const trend = await store.bandTrend();
        if (sessions.length === 0) return { ok: true, message: "No practice yet — start a mock from the main screen and I'll track your estimated band over time." };
        const answered = sessions.reduce((n, s) => n + s.answered_count, 0);
        const lines = [
          `${sessions.length} session${sessions.length !== 1 ? "s" : ""}, ${answered} answer${answered !== 1 ? "s" : ""} recorded. Last practiced ${relativeDays(sessions[0].created_at)}.`,
        ];
        if (trend.length >= 2) {
          const first = trend[0].band_mid;
          const last = trend[trend.length - 1].band_mid;
          const dir = last > first ? "up from" : last < first ? "down from" : "level with";
          lines.push(`Estimated band midpoint: ${last.toFixed(1)} — ${dir} ${first.toFixed(1)} on your first scored session. (Estimated from transcripts; pronunciation isn't scored.)`);
        } else if (trend.length === 1) {
          lines.push(`One scored session so far — estimated band midpoint ${trend[0].band_mid.toFixed(1)}. (Estimated from the transcript; pronunciation isn't scored.)`);
        }
        return { ok: true, message: lines.join("\n\n") };
      }

      case "delete_session":
        await store.deleteSession(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the session"}.`, changed: true };

      case "update_session":
        await store.setTopic(a.id, a.topic);
        return { ok: true, message: `Renamed ${a.label ?? "that session"} to "${a.topic}".`, changed: true };

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // A backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path. One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my practice" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large, use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_to_cloud": {
        // Saves the CURRENT conversation (or a note) to the customer's OWN cloud — distinct from
        // backup_save (the whole collection). Text relay only; Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "library_save": {
        const item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${item.title}" to your library${item.folder ? ` (folder: ${item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const items = libraryList(a.folder);
        if (!items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to research something and save you a plan, or save a lesson from our chat." };
        const lines = items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`);
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + lines.join("\n") };
      }
      case "library_remove": {
        const okRemove = libraryRemove(a.id);
        return okRemove
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      }
      case "library_move": {
        const okMove = libraryMove(a.id, a.folder);
        return okMove
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      }
      case "library_merge": {
        const merged = libraryMerge(a.ids, a.title);
        return merged
          ? { ok: true, message: `Merged into "${merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "practice_questions": {
        // ONE billed call, the app's EXISTING /questions endpoint.
        const q = await getQuestions(a.exam, a.part, a.topic);
        if (a.part === "p2" && q.cue_card) {
          const c = q.cue_card;
          const bullets = (c.bullets ?? []).map((b) => `  • ${b}`).join("\n");
          return { ok: true, message: `Part 2 cue card — ${c.topic}\n\n${bullets}\n\nYou get 1 minute to prepare, then talk for 2 minutes. Start when you're ready.` };
        }
        if (!q.questions.length) return { ok: false, message: "Nothing came back — try naming a topic." };
        const label = a.part === "interview" ? "Interview questions" : `Part ${a.part.slice(1)} questions`;
        return { ok: true, message: `${label}${a.topic ? ` — ${a.topic}` : ""}:\n` + q.questions.map((x, i) => `${i + 1}. ${x}`).join("\n") };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}

export { EXAM_LABELS, MODE_LABELS };
